import { useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateTextDocument,
  useDeleteDocument,
  useDocuments,
  useDocumentText,
  useUpdateTextDocument,
  useUploadDocument,
} from "@/hooks/useDocuments";
import type { DocumentRecord } from "@/hooks/useDocuments";
import { format } from "date-fns";
import { Button, PageHeader } from "@/components/ui";
import { Icon } from "@/components/IconSprite";

const ALL_ROLES = ["guest", "business_owner", "preferred_member", "product_center", "branch_operator", "admin"] as const;

const ROLE_LABELS: Record<string, string> = {
  guest: "Guest",
  business_owner: "Business Owner",
  preferred_member: "Preferred Member",
  product_center: "Product Center",
  branch_operator: "Branch Operator",
  admin: "Admin",
};

const ROLE_BADGE_CLASS: Record<string, string> = {
  guest: "guest",
  business_owner: "bo",
  preferred_member: "pm",
  product_center: "pc",
  branch_operator: "bop",
  admin: "admin",
};

type CreateTab = "upload" | "paste";

interface VisibilityChipsProps {
  selected: string[];
  onToggle: (role: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
}

const VisibilityChips = ({ selected, onToggle, onSelectAll, onClear }: VisibilityChipsProps) => (
  <div>
    <div className="lbl-row">
      <label className="fl">Visibility Roles</label>
      <span style={{ display: "flex", gap: 8 }}>
        <button type="button" className="act-txt" onClick={onSelectAll}>
          Select All
        </button>
        <button type="button" className="act-txt" onClick={onClear}>
          Clear
        </button>
      </span>
    </div>
    <div className="chips">
      {ALL_ROLES.map((role) => (
        <button
          key={role}
          type="button"
          className={`chip${selected.includes(role) ? " on" : ""}`}
          onClick={() => onToggle(role)}
        >
          {ROLE_LABELS[role] ?? role}
        </button>
      ))}
    </div>
    <p className="hint">Only selected roles can retrieve this document in chat.</p>
  </div>
);

interface EditTextModalProps {
  document: DocumentRecord | null;
  onClose: () => void;
}

const EditTextModal = ({ document, onClose }: EditTextModalProps) => {
  const docId = document?.id ?? null;
  const { data, isLoading: loadingBody } = useDocumentText(docId);
  const updateMutation = useUpdateTextDocument();

  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [contextValue, setContextValue] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([...ALL_ROLES]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.document && data?.text !== undefined) {
      setTitle((data.document.filename || "").replace(/\.txt$/i, ""));
      setText(data.text || "");
      setContextValue(data.document.context || "");
      setSelectedRoles(
        data.document.visibility_roles?.length
          ? data.document.visibility_roles
          : [...ALL_ROLES]
      );
      setError(null);
    }
  }, [data]);

  if (!document) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await updateMutation.mutateAsync({
        id: document.id,
        title: title.trim(),
        text: text,
        context: contextValue.trim() || null,
        visibilityRoles: selectedRoles.length ? selectedRoles : [...ALL_ROLES],
      });
      onClose();
    } catch (err: any) {
      setError(String(err?.detail || err?.message || "Failed to save changes."));
    }
  };

  const toggleRole = (role: string) =>
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );

  return (
    <div className="ov open" onClick={onClose}>
      <div className="mod lg" onClick={(e) => e.stopPropagation()}>
        <div className="mod-h">
          <div className="tx">
            <h3>Edit Document</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>

        {loadingBody ? (
          <div className="mod-b">
            <p className="hint">Loading…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mod-b">
              <div className="fg">
                <label className="fl">
                  Title <span className="req">*</span>
                </label>
                <input
                  className="inp"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              <div className="fg">
                <div className="lbl-row">
                  <label className="fl">Content</label>
                  <span className="cnt">{new Blob([text]).size.toLocaleString()} bytes</span>
                </div>
                <textarea
                  className="ta mono"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={14}
                  required
                />
              </div>

              <div className="fg">
                <label className="fl">
                  Context <span className="opt">(optional)</span>
                </label>
                <input
                  className="inp"
                  value={contextValue}
                  onChange={(e) => setContextValue(e.target.value)}
                  placeholder="e.g., Audience: Dealers"
                />
              </div>

              <div className="fg" style={{ marginBottom: 0 }}>
                <VisibilityChips
                  selected={selectedRoles}
                  onToggle={toggleRole}
                  onSelectAll={() => setSelectedRoles([...ALL_ROLES])}
                  onClear={() => setSelectedRoles([])}
                />
              </div>

              {error && <p className="hint err">{error}</p>}
            </div>

            <div className="mod-f">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={updateMutation.isPending}>
                Save Changes
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const ContentLibrary = () => {
  const { data: documents, isLoading } = useDocuments();
  const uploadMutation = useUploadDocument();
  const createTextMutation = useCreateTextDocument();
  const deleteMutation = useDeleteDocument();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [createTab, setCreateTab] = useState<CreateTab>("upload");

  // Upload tab state
  const [contextValue, setContextValue] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>(() => [...ALL_ROLES]);

  // Paste-Text tab state
  const [textTitle, setTextTitle] = useState("");
  const [textBody, setTextBody] = useState("");
  const [textContext, setTextContext] = useState("");
  const [textRoles, setTextRoles] = useState<string[]>(() => [...ALL_ROLES]);
  const [textError, setTextError] = useState<string | null>(null);

  // Edit modal state
  const [editing, setEditing] = useState<DocumentRecord | null>(null);

  const roleLabels = useMemo(() => ROLE_LABELS, []);

  const formatSize = (bytes?: number | null) => {
    if (!bytes || bytes <= 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDelete = (doc: DocumentRecord) => {
    const label = doc.filename || "this document";
    if (!window.confirm(`Delete ${label}? This action cannot be undone.`)) return;
    deleteMutation.mutate(doc.id);
  };

  const handleUpload = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    const trimmedContext = contextValue.trim();
    const roles = selectedRoles.length ? selectedRoles : [...ALL_ROLES];
    uploadMutation.mutate({ file, context: trimmedContext || null, visibilityRoles: roles });
    if (fileInputRef.current) fileInputRef.current.value = "";
    setContextValue("");
    setSelectedRoles([...ALL_ROLES]);
  };

  const handleCreateText = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTextError(null);
    try {
      await createTextMutation.mutateAsync({
        title: textTitle.trim(),
        text: textBody,
        context: textContext.trim() || null,
        visibilityRoles: textRoles.length ? textRoles : [...ALL_ROLES],
      });
      setTextTitle("");
      setTextBody("");
      setTextContext("");
      setTextRoles([...ALL_ROLES]);
    } catch (err: any) {
      setTextError(String(err?.detail || err?.message || "Failed to create document."));
    }
  };

  const toggleRole = (role: string) =>
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  const toggleTextRole = (role: string) =>
    setTextRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );

  return (
    <div className="page">
      <PageHeader
        label="Knowledge Base"
        title="Content Library"
        description="Upload files or paste text to feed the assistant's retrieval. Supported file formats: PDF, TXT, Markdown, CSV."
      />

      {/* Create — tabs */}
      <div className="card">
        <div className="card-h tight">
          <div>
            <h2>Add Content</h2>
            <p>Upload a file or paste text directly. Both go through the same chunking + embedding pipeline.</p>
          </div>
        </div>

        <div className="tabs">
          <button
            type="button"
            className={`tab${createTab === "upload" ? " on" : ""}`}
            onClick={() => setCreateTab("upload")}
          >
            <Icon name="upload" />
            Upload File
          </button>
          <button
            type="button"
            className={`tab${createTab === "paste" ? " on" : ""}`}
            onClick={() => setCreateTab("paste")}
          >
            <Icon name="txt" />
            Paste Text
          </button>
        </div>

        {createTab === "upload" ? (
          <form onSubmit={handleUpload}>
            <div className="card-b">
              <div className="md" style={{ gridTemplateColumns: "1fr 320px" }}>
                <div>
                  <div className="dz" onClick={() => fileInputRef.current?.click()}>
                    <div className="dz-i">
                      <Icon name="upload" />
                    </div>
                    <b>Click to upload or drag and drop</b>
                    <span>PDF, TXT, Markdown, CSV · up to 20 MB</span>
                  </div>
                  <input ref={fileInputRef} type="file" className="hidden" required />

                  <div className="fg" style={{ marginTop: 14 }}>
                    <label className="fl">
                      Context <span className="opt">(optional)</span>
                    </label>
                    <input
                      className="inp"
                      value={contextValue}
                      onChange={(e) => setContextValue(e.target.value)}
                      placeholder="e.g., Audience: Dealers"
                    />
                  </div>
                </div>

                <div>
                  <VisibilityChips
                    selected={selectedRoles}
                    onToggle={toggleRole}
                    onSelectAll={() => setSelectedRoles([...ALL_ROLES])}
                    onClear={() => setSelectedRoles([])}
                  />
                  <Button
                    type="submit"
                    loading={uploadMutation.isPending}
                    style={{ width: "100%", marginTop: 14 }}
                  >
                    <Icon name="upload" />
                    Upload Document
                  </Button>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <form onSubmit={handleCreateText}>
            <div className="card-b">
              <div className="md" style={{ gridTemplateColumns: "1fr 320px" }}>
                <div>
                  <div className="fg">
                    <label className="fl">
                      Title <span className="req">*</span>
                    </label>
                    <input
                      className="inp"
                      value={textTitle}
                      onChange={(e) => setTextTitle(e.target.value)}
                      placeholder="e.g., Customer Support Email"
                      required
                    />
                  </div>

                  <div className="fg">
                    <div className="lbl-row">
                      <label className="fl">
                        Content <span className="req">*</span>
                      </label>
                      <span className="cnt">{new Blob([textBody]).size.toLocaleString()} bytes</span>
                    </div>
                    <textarea
                      className="ta mono"
                      value={textBody}
                      onChange={(e) => setTextBody(e.target.value)}
                      rows={10}
                      placeholder="Paste the content the assistant should learn from..."
                      required
                    />
                  </div>

                  <div className="fg">
                    <label className="fl">
                      Context <span className="opt">(optional)</span>
                    </label>
                    <input
                      className="inp"
                      value={textContext}
                      onChange={(e) => setTextContext(e.target.value)}
                      placeholder="e.g., Audience: Dealers"
                    />
                  </div>
                </div>

                <div>
                  <VisibilityChips
                    selected={textRoles}
                    onToggle={toggleTextRole}
                    onSelectAll={() => setTextRoles([...ALL_ROLES])}
                    onClear={() => setTextRoles([])}
                  />
                  {textError && <p className="hint err">{textError}</p>}
                  <Button
                    type="submit"
                    loading={createTextMutation.isPending}
                    style={{ width: "100%", marginTop: 14 }}
                  >
                    <Icon name="plus" />
                    Create Document
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Documents Table */}
      <div className="card">
        <div className="card-h tight">
          <div>
            <h2>
              Documents{" "}
              <span className="b def">
                {documents ? `${documents.length} items` : isLoading ? "Loading..." : "No content"}
              </span>
            </h2>
          </div>
        </div>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th style={{ width: "30%" }}>Filename</th>
                <th>Context</th>
                <th>Visibility</th>
                <th style={{ width: 80 }}>Size</th>
                <th style={{ width: 110 }}>Uploaded</th>
                <th style={{ width: 90, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents?.map((doc) => {
                const roles = doc.visibility_roles ?? [];
                const firstRole = roles[0];
                return (
                  <tr key={doc.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <span className="stat-i" style={{ width: 24, height: 24, borderRadius: 6 }}>
                          <Icon name="file" />
                        </span>
                        <div className="pri-cell">{doc.filename}</div>
                      </div>
                    </td>
                    <td>{doc.context?.trim() || "—"}</td>
                    <td>
                      {roles.length ? (
                        <>
                          <span className={`rb ${ROLE_BADGE_CLASS[firstRole] ?? "guest"}`}>
                            {roleLabels[firstRole] ?? firstRole}
                          </span>
                          {roles.length > 1 && (
                            <span className="b def" style={{ marginLeft: 4 }}>
                              +{roles.length - 1}
                            </span>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="num">{formatSize(doc.size_bytes)}</td>
                    <td className="num">{format(new Date(doc.created_at), "MMM d, yyyy")}</td>
                    <td>
                      <div className="acts">
                        {doc.editable && (
                          <button type="button" className="act" title="Edit" onClick={() => setEditing(doc)}>
                            <Icon name="edit" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="act d"
                          title="Delete"
                          onClick={() => handleDelete(doc)}
                          disabled={deleteMutation.isPending}
                        >
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!documents?.length && !isLoading && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty sm">
                      <div className="empty-i">
                        <Icon name="inbox" />
                      </div>
                      <b>No documents yet</b>
                      <p>Upload or paste your first knowledge source above.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EditTextModal document={editing} onClose={() => setEditing(null)} />
    </div>
  );
};

export default ContentLibrary;
